import React, { useState } from "react";
import { Card, Space, Tag, Typography, Select, InputNumber, Button } from "antd";
import { ToolOutlined } from "@ant-design/icons";
import { updateCameraPreventive } from "../network/api";
import { notify, serverError } from "../utils/notify";
import { useAuth } from "../context/AuthContext";
import { relativeTime } from "../constants/health";
import dayjs from "dayjs";

const { Text } = Typography;

// D2-T4 — per-device preventive-maintenance: shows the effective interval + next
// due date, and lets an admin override the interval (or disable auto-scheduling).
export default function PreventiveCard({ device, onChange }) {
  const { isHardwareAdmin } = useAuth();
  const p = device.preventive || {};
  const initialMode = p.intervalDays === 0 ? "disabled" : p.intervalDays == null ? "default" : "custom";
  const [mode, setMode] = useState(initialMode);
  const [days, setDays] = useState(p.intervalDays > 0 ? p.intervalDays : p.defaultIntervalDays || 90);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const value = mode === "default" ? null : mode === "disabled" ? 0 : days;
    setSaving(true);
    try {
      await updateCameraPreventive(device._id, value);
      notify.success("Preventive schedule updated");
      onChange?.();
    } catch (error) {
      notify.error(serverError(error, "Failed to update schedule"));
    } finally {
      setSaving(false);
    }
  };

  const statusLine = p.disabled ? (
    <Tag>Auto-scheduling disabled</Tag>
  ) : (
    <Space wrap>
      <Text>
        Every <Text strong>{p.effectiveIntervalDays}</Text> days
        {p.intervalDays == null ? <Text type="secondary"> (default)</Text> : null}
      </Text>
      {p.nextDueAt && (
        p.overdue ? (
          <Tag color="red">Overdue (was due {relativeTime(p.nextDueAt)})</Tag>
        ) : (
          <Tag color="green">Next due {dayjs(p.nextDueAt).format("YYYY-MM-DD")} ({relativeTime(p.nextDueAt)})</Tag>
        )
      )}
      {p.lastPreventiveAt && (
        <Text type="secondary">Last: {relativeTime(p.lastPreventiveAt)}</Text>
      )}
    </Space>
  );

  return (
    <Card title="Preventive Maintenance" extra={<ToolOutlined style={{ color: "#9aa5b8" }} />}>
      <div style={{ marginBottom: isHardwareAdmin ? 14 : 0 }}>{statusLine}</div>
      {isHardwareAdmin && (
        <Space wrap align="center">
          <Select
            style={{ width: 200 }}
            value={mode}
            onChange={setMode}
            options={[
              { value: "default", label: `Use default (${p.defaultIntervalDays || 90} days)` },
              { value: "custom", label: "Custom interval…" },
              { value: "disabled", label: "Disable auto-scheduling" },
            ]}
          />
          {mode === "custom" && (
            <InputNumber min={1} value={days} onChange={setDays} addonAfter="days" style={{ width: 130 }} />
          )}
          <Button type="primary" loading={saving} onClick={save}>Save</Button>
        </Space>
      )}
    </Card>
  );
}
